import React, { useState, useRef } from 'react';
import { object } from 'prop-types';
import classNames from 'classnames';
import { noop } from 'lodash';
import { Base } from '@fiverr-private/modal/src';
import { Button, Icon } from '@fiverr-private/fit';
import { Stack } from '@fiverr-private/layout_components';
import { Typography } from '@fiverr-private/typography';
import { PlayOutlineSolidIcon } from '@fiverr-private/icons';
import { ChevronButton } from '@fiverr-private/button';
import { tracker as listingsTracker } from '@fiverr-private/listing_lib';
import ModalBody from '../ModalBody';
import { GENERAL_VIDEO_MODAL_NAME, VIDEO_PROP_TYPES } from '../constants';
import { useTextOverflowDetection } from './useTextOverflowDetection';

import './index.scss';

const ExplanationVideo = ({
    subcat = {},
    onOpen = noop,
    className,
    video,
    videoTitle,
    sourceInfo,
    showButton = true,
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const subtitleRef = useRef(null);

    const { isOverflowing, hasCheckedOverflow } = useTextOverflowDetection(subtitleRef, subcat.subtitle);

    const toggleSubtitle = () => {
        setIsExpanded((prev) => !prev);
    };

    const handleOpen = () => {
        if (onOpen !== noop) {
            onOpen();
        } else {
            const { categoryName, title } = subcat;

            listingsTracker.trackHowFiverrWorksVideo({ categoryName, subcategoryName: title });
        }
    };

    return (
        <Stack
            className={classNames('explanation-video', className)}
            direction={{ default: 'column', sm: 'row' }}
            alignItems={{ default: 'flexStart', sm: 'center' }}
            justifyContent="flexStart"
            flexWrap={{ default: 'wrap', sm: 'nowrap' }}
            gap={{ default: '1', sm: '0' }}
            width="100%"
        >
            {subcat.subtitle && (
                <Stack
                    className="subtitle-wrapper"
                    direction="row"
                    alignItems="flexStart"
                    alignSelf="center"
                    flex="0 1 auto"
                    minWidth={0}
                    maxWidth="100%"
                    columnGap="1"
                >
                    <Typography
                        as="p"
                        ref={subtitleRef}
                        className={classNames('sc-subtitle', { collapsed: isOverflowing && !isExpanded })}
                        margin="0"
                        flex="1 1 auto"
                        minWidth={0}
                        maxWidth="100%"
                        wordWrap="break-word"
                    >
                        {subcat.subtitle}
                    </Typography>
                    {hasCheckedOverflow && isOverflowing && (
                        <ChevronButton
                            onClick={toggleSubtitle}
                            size="2xs"
                            chevronDirection={isExpanded ? 'up' : 'down'}
                            aria-label="Toggle subtitle"
                        />
                    )}
                </Stack>
            )}
            {subcat.subtitle && showButton && (
                <Stack
                    className="separator"
                    display={{ default: 'none', sm: 'flex' }}
                    alignSelf={isExpanded ? 'stretch' : 'center'}
                    height={isExpanded ? 'auto' : '22px'}
                    justifyContent="center"
                    width="2px"
                    backgroundColor="grey_400"
                    marginLeft="3"
                    marginRight="1"
                    flexShrink={0}
                />
            )}
            {showButton && (
                <Stack alignSelf="flexStart">
                    <Base
                        name={GENERAL_VIDEO_MODAL_NAME}
                        onOpen={handleOpen}
                        trigger={({ open }) => (
                            <Button className="btn-play" size={Button.SIZES.SMALL} onClick={open}>
                                <Icon size={14} className="play-icon">
                                    <PlayOutlineSolidIcon />
                                </Icon>
                                {videoTitle}
                            </Button>
                        )}
                    >
                        {() => <ModalBody video={video} sourceInfo={sourceInfo} />}
                    </Base>
                </Stack>
            )}
        </Stack>
    );
};

ExplanationVideo.propTypes = {
    ...VIDEO_PROP_TYPES,
    subcat: object,
};

export default ExplanationVideo;
